import React, { useEffect, useMemo } from "react";
import { RiContractRightLine } from "@remixicon/react";
import { Typography } from "../../atoms";
import ContentQualitySectionCard from "./ContentQualitySectionCard";
import AudienceFitCard from "./AudienceFitCard";
import StickyRecommendationsCard from "./StickyRecommendationsCard";
import ContentQualityTabs from "./ContentQualityTabs";
import { ContentQualityWidgetProps } from "./types";
import styles from "./index.style.module.css";

// Re-export types for convenience
export * from "./types";

/**
 * ContentQualityWidget displays comprehensive content quality analysis
 * in a sliding side panel similar to the control panel.
 * Fixed width: 574px
 */
const ContentQualityWidget: React.FC<ContentQualityWidgetProps> = ({
  contentQualityState,
  contentQualityFilledSegments,
  qualityMetrics,
  audienceFit,
  stickyRecommendations,
  tabsContent,
  onApplyClick,
  onRecommendationToggle,
  isOpen,
  onClose,
  className = "",
}) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  // Check if there are any recommendations
  const hasRecommendations = useMemo(() => {
    const totalRecommendations =
      stickyRecommendations.contentQualityTotal +
      stickyRecommendations.audienceFitTotal;
    return totalRecommendations > 0;
  }, [stickyRecommendations]);

  // Determine title based on whether there are recommendations
  const headerTitle = hasRecommendations
    ? "Improve Content"
    : "Your content looks healthy!";

  return (
    <aside
      className={`${styles.qualityPanel} ${
        isOpen ? styles.qualityPanelVisible : styles.qualityPanelHidden
      } ${className}`}
      hidden={!isOpen}
    >
      {/* Close Button */}
      {isOpen && (
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close content quality panel"
          type="button"
        >
          <RiContractRightLine size={20} />
        </button>
      )}

      {isOpen && (
        <div className={styles.panelContent}>
          {/* Header Section - Title and Subtitle */}
          <div className={styles.header}>
            <Typography
              variant="display-xs"
              weight="bold"
              className={styles.title}
            >
              {headerTitle}
            </Typography>
            {hasRecommendations && (
              <Typography
                variant="text-s"
                weight="regular"
                className={styles.subtitle}
              >
                Apply these recommendations to improve your content
              </Typography>
            )}
          </div>

          {/* Content Quality Section */}
          <ContentQualitySectionCard
            state={contentQualityState}
            filledSegments={contentQualityFilledSegments}
            qualityMetrics={qualityMetrics}
          />

          {/* Audience Fit Section - Only shown when audienceFit data exists */}
          {audienceFit && (
            <AudienceFitCard
              confidenceValue={audienceFit.confidenceValue}
              status={audienceFit.status}
              metrics={audienceFit.metrics}
              worksWell={audienceFit.worksWell}
            />
          )}

          <div>
            {/* Sticky Recommendations Card */}
            <div className={hasRecommendations ? styles.stickySection : ""}>
              <StickyRecommendationsCard
                audienceFitCount={stickyRecommendations.audienceFitCount}
                audienceFitTotal={stickyRecommendations.audienceFitTotal}
                contentQualityCount={stickyRecommendations.contentQualityCount}
                contentQualityTotal={stickyRecommendations.contentQualityTotal}
                onApplyClick={onApplyClick}
              />
            </div>

            {/* Tabs Section (Recommendation Groups) - Only shown when there are recommendations */}
            {hasRecommendations && (
              <div className={styles.tabsSection}>
                <ContentQualityTabs
                  tabs={tabsContent}
                  onToggle={onRecommendationToggle}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
};

ContentQualityWidget.displayName = "ContentQualityWidget";

export default ContentQualityWidget;
